/**
 * Hybrid Stack Component
 * Automatically chooses between Tailwind and styled-components based on props.
 *
 * Routes to the styled-components path when a consumer passes styled-system
 * props (e.g. p, bg, color), a styled() extension, or responsive (array) /
 * arbitrary-string values for space/align/direction — none of which can be
 * expressed as a static CSS class. Otherwise renders the Tailwind path.
 */

import React from "react";
import type { TypeStackProps } from "./StackTypes";
import StackStyledComponents from "./StackStyledComponents"; // Styled-components version
import { StackTailwind } from "./StackTailwind"; // Tailwind version
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";

const isResponsive = (value: unknown): boolean => Array.isArray(value);

const StackHybrid = React.forwardRef<HTMLDivElement, TypeStackProps>(
  (props, ref) => {
    const { space, align, direction, children, ...rest } = props;

    const needsStyledComponents =
      hasStyledProps(rest) ||
      isResponsive(space) ||
      isResponsive(align) ||
      isResponsive(direction) ||
      // arbitrary string spacing (e.g. "20px") can't map to a variant class
      typeof space === "string";

    if (needsStyledComponents) {
      return <StackStyledComponents ref={ref} {...props} />;
    }

    // Use Tailwind version (preferred - better performance)
    return <StackTailwind ref={ref} {...props} />;
  }
);

StackHybrid.displayName = "Stack";
export default StackHybrid;
